import {
  Autocomplete,
  type Item,
  SelectInput,
  SelectInputItem,
  Switch,
  TextField,
} from '@ads/components-react';
import { useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCompaniesDropdown } from '@/features/student-management/hooks/useCompaniesDropdown';
import {
  useGetApiV1PersonSharedAcademicEducationDropdown,
  useGetApiV1PersonSharedBusinessSectorDropdown,
  useGetApiV1PersonSharedExperienceLevelDropdown,
  useGetApiV1PersonSharedServiceTimeDropdown,
} from '@/shared/api/generated';
import { DatePicker } from '@/shared/ui/atoms';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';

import { numToStr, strToNum, type TabProps } from '../personFormSchema';

const OTHER_ID = 1;

export function ProfessionalTab({ control, register, setValue }: TabProps) {
  const { t } = useTranslation('personManagement');
  const { t: tCommon } = useTranslation();

  const { data: serviceTimeOptions = [] } = useGetApiV1PersonSharedServiceTimeDropdown();
  const { data: experienceLevelOptions = [] } = useGetApiV1PersonSharedExperienceLevelDropdown();
  const { data: academicEducationOptions = [] } =
    useGetApiV1PersonSharedAcademicEducationDropdown();
  const { data: businessSectorData = [] } = useGetApiV1PersonSharedBusinessSectorDropdown();

  const associatedCompanyId = useWatch({ control, name: 'associatedCompanyId' });
  const businessSectorId = useWatch({ control, name: 'businessSectorId' });
  const watchedCompanyName = useWatch({ control, name: 'company' });

  const isOtherCompany = associatedCompanyId === OTHER_ID;
  const isOtherSector = businessSectorId === OTHER_ID;

  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompanyLabel, setSelectedCompanyLabel] = useState('');

  const { companies, isQuerySuccess: isCompanyQuerySuccess } = useCompaniesDropdown(companySearch);

  const companyItems: Item<number>[] = companies.map(c => ({
    label: c.name,
    value: Number(c.id),
  }));

  const companyLabelToShow =
    selectedCompanyLabel || (!isOtherCompany && watchedCompanyName ? watchedCompanyName : '');

  const selectedCompany: Item<number> | null =
    associatedCompanyId !== undefined
      ? { label: companyLabelToShow, value: associatedCompanyId }
      : null;

  const companyMessage = getAutocompleteSearchMessage(
    companySearch.length,
    isCompanyQuerySuccess,
    companyItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  const businessSectorItems: Item<number>[] = businessSectorData.map(({ key, value }) => ({
    label: value ?? '',
    value: key ?? 0,
  }));

  const selectedSector = businessSectorItems.find(i => i.value === businessSectorId) ?? null;

  return (
    <div className="flex flex-col gap-xxs">
      {/* Empresa e profissão */}
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <TextField
          fullWidth
          size="md"
          label={t('personForm.fields.profession')}
          placeholder={t('personForm.fields.profession')}
          {...register('profession')}
        />
        <Autocomplete<number>
          size="md"
          label={t('personForm.fields.company')}
          placeholder={t('personForm.fields.company')}
          list={companyItems}
          {...(companyMessage !== undefined ? { notFoundMessage: companyMessage } : {})}
          {...(selectedCompany !== null ? { selectedItem: selectedCompany } : {})}
          onChange={e => setCompanySearch(e.target.value)}
          handleChange={item => {
            setValue?.('associatedCompanyId', item?.value ?? undefined);
            setValue?.('company', item?.value === OTHER_ID ? null : (item?.label ?? null));
            setSelectedCompanyLabel(item?.label ?? '');
            setCompanySearch('');
          }}
          handleClearQuery={() => {
            setValue?.('associatedCompanyId', undefined);
            setValue?.('company', null);
            setSelectedCompanyLabel('');
            setCompanySearch('');
          }}
        />
      </div>
      {isOtherCompany && (
        <TextField
          fullWidth
          size="md"
          label={t('personForm.fields.companyName')}
          placeholder={t('personForm.fields.companyName')}
          {...register('company')}
        />
      )}

      {/* Cargo e setor */}
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <TextField
          fullWidth
          size="md"
          label={t('personForm.fields.otherJobPosition')}
          placeholder={t('personForm.fields.otherJobPosition')}
          {...register('otherJobPosition')}
        />
        <Autocomplete<number>
          size="md"
          label={t('personForm.fields.otherBusinessSector')}
          placeholder={t('personForm.fields.otherBusinessSector')}
          list={businessSectorItems}
          {...(selectedSector !== null ? { selectedItem: selectedSector } : {})}
          handleChange={item => {
            setValue?.('businessSectorId', item?.value ?? undefined);
            setValue?.(
              'otherBusinessSector',
              item?.value === OTHER_ID ? null : (item?.label ?? null),
            );
          }}
        />
      </div>
      {isOtherSector && (
        <TextField
          fullWidth
          size="md"
          label={t('personForm.fields.businessSectorName')}
          placeholder={t('personForm.fields.businessSectorName')}
          {...register('otherBusinessSector')}
        />
      )}

      {/* Tempo de serviço e nível */}
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <Controller
          name="serviceTime"
          control={control}
          render={({ field }) => (
            <SelectInput
              size="md"
              label={t('personForm.fields.serviceTime')}
              placeholder={t('personForm.fields.serviceTime')}
              value={numToStr(field.value)}
              onValueChange={v => field.onChange(strToNum(v))}
            >
              {serviceTimeOptions.map(({ key, value }) => (
                <SelectInputItem key={key} value={String(key)} custom={{ root: 'text-nowrap' }}>
                  {value ?? ''}
                </SelectInputItem>
              ))}
            </SelectInput>
          )}
        />
        <Controller
          name="experienceLevel"
          control={control}
          render={({ field }) => (
            <SelectInput
              size="md"
              label={t('personForm.fields.experienceLevel')}
              placeholder={t('personForm.fields.experienceLevel')}
              value={numToStr(field.value)}
              onValueChange={v => field.onChange(strToNum(v))}
            >
              {experienceLevelOptions.map(({ key, value }) => (
                <SelectInputItem key={key} value={String(key)} custom={{ root: 'text-nowrap' }}>
                  {value ?? ''}
                </SelectInputItem>
              ))}
            </SelectInput>
          )}
        />
      </div>

      {/* Situação profissional */}
      <div className="flex flex-wrap gap-xs rounded-md border-hairline border-ctx-layout-border p-mini">
        <Controller
          name="isWorking"
          control={control}
          render={({ field }) => (
            <Switch
              label={t('personForm.fields.isWorking')}
              checked={field.value === true}
              onCheckedChange={field.onChange}
            />
          )}
        />
        <Controller
          name="selfEmployed"
          control={control}
          render={({ field }) => (
            <Switch
              label={t('personForm.fields.selfEmployed')}
              checked={field.value === true}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </div>

      {/* Formação acadêmica */}
      <h3 className="ts-heading-xs mt-xxs text-ctx-content-title">
        {t('personForm.fields.academicFormation')}
      </h3>
      <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border p-mini">
        <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
          <Controller
            name="academicEducation"
            control={control}
            render={({ field }) => (
              <SelectInput
                size="md"
                label={t('personForm.fields.academicEducation')}
                placeholder={t('personForm.fields.academicEducation')}
                value={numToStr(field.value)}
                onValueChange={v => field.onChange(strToNum(v))}
              >
                {academicEducationOptions.map(({ key, value }) => (
                  <SelectInputItem key={key} value={String(key)} custom={{ root: 'text-nowrap' }}>
                    {value ?? ''}
                  </SelectInputItem>
                ))}
              </SelectInput>
            )}
          />
          <Controller
            name="completionDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                size="md"
                label={t('personForm.fields.completionDate')}
                value={field.value ?? ''}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
          <TextField
            fullWidth
            size="md"
            label={t('personForm.fields.academicCourse')}
            placeholder={t('personForm.fields.academicCourse')}
            {...register('academicCourseName')}
          />
        </div>
      </div>
    </div>
  );
}
